<script>
  import { navigate } from 'svelte-routing';
  import { user } from '../stores/authStore';

  const CURRENCY_MAP = {
    AU: { code: 'AUD', symbol: '$' },
    NZ: { code: 'NZD', symbol: '$' },
    US: { code: 'USD', symbol: '$' },
    GB: { code: 'GBP', symbol: '£' },
    CA: { code: 'CAD', symbol: '$' }
  };

  $: currency = ($user?.country && CURRENCY_MAP[$user.country])
    ? CURRENCY_MAP[$user.country]
    : { code: 'USD', symbol: '$' };

  function selectPlan(plan) {
    if ($user) {
      navigate(`/settings?plan=${plan}`);
    } else {
      navigate(`/login?redirect=/settings&plan=${plan}`);
    }
  }
</script>

<div class="min-h-screen bg-gray-50">
  <!-- Header -->
  <div class="bg-[#363f59] py-6">
    <div class="max-w-4xl mx-auto px-4 flex items-center justify-between">
      <a href="/login" class="flex items-center">
        <img class="h-10 rounded-md" src="/images/buildzilla-logo-sm.jpg" alt="Buildzilla">
      </a>
      {#if $user}
        <a href="/dashboard" class="text-white text-sm hover:text-gray-200 underline">Back to App</a>
      {:else}
        <a href="/login" class="text-white text-sm hover:text-gray-200 underline">Log in</a>
      {/if}
    </div>
  </div>

  <div class="max-w-4xl mx-auto px-4 py-12">
    <!-- Title -->
    <div class="text-center mb-10">
      <h1 class="text-3xl font-bold text-gray-900">Simple, transparent pricing</h1>
      <p class="mt-3 text-lg text-gray-500">Choose the plan that fits your business</p>

      <p class="mt-4 text-sm text-gray-500">Prices shown in {currency.code}. Billed monthly.</p>
    </div>

    <!-- Plans -->
    <div class="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
      <!-- Standard -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col">
        <h2 class="text-xl font-semibold text-gray-900">Standard</h2>
        <div class="mt-4 flex items-baseline">
          <span class="text-4xl font-bold text-gray-900">{currency.symbol}40</span>
          <span class="ml-1 text-gray-500">/ month</span>
        </div>
        <p class="mt-2 text-sm text-gray-500">{currency.code}, billed monthly</p>

        <ul class="mt-8 space-y-4 flex-1">
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span class="text-gray-700">Xero Sync for bills and invoices</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span class="text-gray-700">Automatic status updates on payment events</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span class="text-gray-700">Cost Code to Xero Account mappings</span>
          </li>
        </ul>

        <button
          on:click={() => selectPlan('standard')}
          class="mt-8 w-full py-3 px-4 rounded-lg border-2 border-[#363f59] text-[#363f59] font-semibold hover:bg-[#363f59] hover:text-white transition-colors"
        >
          Select Standard
        </button>
      </div>

      <!-- Plus -->
      <div class="bg-white rounded-xl shadow-md border-2 border-blue-500 p-8 flex flex-col relative">
        <div class="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span class="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Most Popular</span>
        </div>

        <h2 class="text-xl font-semibold text-gray-900">Plus</h2>
        <div class="mt-4 flex items-baseline">
          <span class="text-4xl font-bold text-gray-900">{currency.symbol}59</span>
          <span class="ml-1 text-gray-500">/ month</span>
        </div>
        <p class="mt-2 text-sm text-gray-500">{currency.code}, billed monthly</p>

        <ul class="mt-8 space-y-4 flex-1">
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span class="text-gray-700">Everything in Standard</span>
          </li>
          <li class="flex items-start">
            <svg class="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span class="text-gray-700">Cost Reporting Analysis on in-flight jobs within JobTread</span>
          </li>
        </ul>

        <button
          on:click={() => selectPlan('plus')}
          class="mt-8 w-full py-3 px-4 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors border-0"
        >
          Select Plus
        </button>
      </div>
    </div>

    <p class="text-center text-sm text-gray-400 mt-10">
      All plans are billed monthly. Cancel anytime.
    </p>
  </div>
</div>
